/**
 * Manager Dashboard API Client
 * Team activity and developer health metrics (Phase 7)
 */

import { apiFetch } from './config';

// Types for team activity data
export interface DeveloperActivity {
  email_address: string;
  display_name: string | null;
  sessions: {
    last_30_days: number;
    last_7_days: number;
    last_session: string | null;
  };
  commits: {
    last_30_days: number;
    last_7_days: number;
    last_commit: string | null;
    days_since_commit: number | null;
  };
  prs_merged_30d: number;
  session_to_commit_conversion_pct: number;
  status: 'active' | 'stale' | 'very_stale' | 'no_ai_usage' | 'low_output';
}

export interface TeamActivitySummary {
  total_developers: number;
  active_developers: number;
  stale_developers: number;
  very_stale_developers: number;
  avg_sessions_30d: number;
  avg_commits_30d: number;
  avg_conversion_pct: number;
}

export interface TeamActivityResponse {
  company_id: string;
  summary: TeamActivitySummary;
  developers: DeveloperActivity[];
}

// Types for patterns data
export interface PatternSummary {
  pattern_id: string;
  pattern_name: string;
  category: string;
  status: 'provisional' | 'validated' | 'reinforced';
  times_applied: number;
  created_by: string;
  created_at: string;
  last_applied: string | null;
}

export interface PatternStats {
  total_patterns: number;
  provisional: number;
  validated: number;
  reinforced: number;
  patterns_this_week: number;
}

// Types for alerts data
export interface DeveloperAlert {
  alert_id: string;
  alert_type: 'inactivity' | 'low_output' | 'violation' | 'burnout_risk' | 'struggling';
  severity: 'info' | 'warning' | 'error';
  message: string;
  developer_email: string;
  developer_name: string | null;
  project_name: string | null;
  created_at: string;
  acknowledged: boolean;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
}

export interface AlertsSummary {
  total_alerts: number;
  unacknowledged: number;
  by_severity: {
    info: number;
    warning: number;
    error: number;
  };
  by_type: {
    inactivity: number;
    low_output: number;
    violation: number;
    burnout_risk: number;
    struggling: number;
  };
}

/**
 * Fetch team activity data
 */
export const getTeamActivity = async (
  companyId?: string
): Promise<{ success: boolean; data?: TeamActivityResponse; error?: string }> => {
  const params = new URLSearchParams();
  if (companyId) params.append('company_id', companyId);

  const query = params.toString();
  const response = await apiFetch<any>(
    `/api/activity/team${query ? `?${query}` : ''}`
  );

  if (response.success && response.data) {
    // Unwrap response envelope: { success, message, data: { Records: [...] }, meta }
    const records = response.data?.data?.Records || response.data?.Records || [];
    if (records[0]) {
      return { success: true, data: records[0] };
    }
  }

  return { success: response.success, error: response.error || 'No team activity data' };
};

/**
 * Fetch pattern stats
 */
export const getPatternStats = async (
  companyId?: string
): Promise<{ success: boolean; data?: PatternStats; patterns?: PatternSummary[]; error?: string }> => {
  const params = new URLSearchParams();
  if (companyId) params.append('company_id', companyId);

  const query = params.toString();

  const response = await apiFetch<any>(
    `/api/analytics/activity-summary?period=30d${query ? `&${query}` : ''}`
  );

  if (response.success && response.data) {
    const data = response.data?.data || response.data;
    const promotions = data?.promotions || {};
    const harvests = data?.harvests || {};
    return {
      success: true,
      data: {
        total_patterns: (promotions.proposed || 0) + (promotions.active || 0),
        provisional: promotions.proposed || 0,
        validated: promotions.active || 0,
        reinforced: 0,
        patterns_this_week: harvests.total || 0,
      },
      patterns: [],
    };
  }

  return { success: true, data: { total_patterns: 0, provisional: 0, validated: 0, reinforced: 0, patterns_this_week: 0 }, patterns: [] };
};

/**
 * Fetch alerts for the team
 */
export const getTeamAlerts = async (
  options?: {
    companyId?: string;
    acknowledged?: boolean;
    limit?: number;
  }
): Promise<{ success: boolean; data?: { summary: AlertsSummary; alerts: DeveloperAlert[] }; error?: string }> => {
  const params = new URLSearchParams();
  if (options?.companyId) params.append('company_id', options.companyId);
  if (options?.acknowledged !== undefined) params.append('acknowledged', String(options.acknowledged));
  if (options?.limit) params.append('limit', String(options.limit));

  const query = params.toString();
  const response = await apiFetch<any>(
    `/api/alerts${query ? `?${query}` : ''}`
  );

  if (response.success && response.data) {
    // Unwrap response envelope
    const innerData = response.data?.data || response.data;
    return { success: true, data: innerData };
  }

  return { success: false, error: response.error };
};

/**
 * Acknowledge an alert
 */
export const acknowledgeAlert = async (
  alertId: string
): Promise<{ success: boolean; error?: string }> => {
  return apiFetch('/api/alerts/acknowledge', {
    method: 'POST',
    body: JSON.stringify({ alert_id: alertId }),
  });
};
